// Ticket status enum
export type TicketStatus =
  | 'pending'
  | 'in_progress'
  | 'replied'
  | 'waiting_confirm'
  | 'closed'
  | 'reopened';

// Ticket priority enum
export type TicketPriority = 'low' | 'medium' | 'high' | 'urgent';

// Notification type
export type TicketNotificationType =
  | 'ticket_created'
  | 'ticket_replied'
  | 'ticket_status_changed'
  | 'ticket_assigned'
  | 'ticket_closed'
  | 'ticket_sla_warning';

// Status display mapping
export const TICKET_STATUS_MAP: Record<TicketStatus, string> = {
  pending: '待处理',
  in_progress: '处理中',
  replied: '已回复',
  waiting_confirm: '待确认',
  closed: '已关闭',
  reopened: '已重开',
};

// Status color mapping
export const TICKET_STATUS_COLORS: Record<TicketStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-blue-100 text-blue-800',
  replied: 'bg-green-100 text-green-800',
  waiting_confirm: 'bg-purple-100 text-purple-800',
  closed: 'bg-gray-100 text-gray-800',
  reopened: 'bg-orange-100 text-orange-800',
};

// Priority display mapping
export const TICKET_PRIORITY_MAP: Record<TicketPriority, string> = {
  low: '低',
  medium: '中',
  high: '高',
  urgent: '紧急',
};

// Priority color mapping
export const TICKET_PRIORITY_COLORS: Record<TicketPriority, string> = {
  low: 'bg-gray-100 text-gray-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

// Notification type mapping
export const NOTIFICATION_TYPE_MAP: Record<TicketNotificationType, string> = {
  ticket_created: '工单创建',
  ticket_replied: '工单回复',
  ticket_status_changed: '状态变更',
  ticket_assigned: '工单分配',
  ticket_closed: '工单关闭',
  ticket_sla_warning: 'SLA预警',
};

export interface TicketCategory {
  id: number;
  name: string;
  slug: string;
  is_active: boolean;
  sort_order: number;
}

export interface TicketLabel {
  id: number;
  name: string;
  color: string;
}

export interface TicketAttachment {
  id: string;
  file_url: string;
  thumbnail_url: string | null;
  original_filename: string;
  file_size: number;
  created_at: string;
}

export interface TicketMessage {
  id: string;
  sender: number;
  sender_name: string;
  sender_type: 'user' | 'admin' | 'system';
  content: string;
  is_internal: boolean;
  attachments: TicketAttachment[];
  created_at: string;
}

export interface TicketStatusLog {
  id: number;
  old_status: string;
  new_status: string;
  changed_by: number;
  changed_by_name: string;
  note: string;
  created_at: string;
}

export interface Ticket {
  id: string;
  title: string;
  description: string;
  category: number | null;
  category_name: string;
  priority?: TicketPriority;
  priority_display?: string;
  status: TicketStatus;
  status_display: string;
  creator: number;
  creator_name: string;
  assignee: number | null;
  assignee_name: string;
  labels: TicketLabel[];
  message_count: number;
  sla_due_at?: string | null;
  sla_breached?: boolean;
  satisfaction_rating: number | null;
  satisfaction_comment: string;
  rated_at: string | null;
  messages?: TicketMessage[];
  status_logs?: TicketStatusLog[];
  created_at: string;
  updated_at: string;
  closed_at: string | null;
}

export interface TicketNotification {
  id: number;
  ticket: string;
  ticket_title: string;
  type: TicketNotificationType;
  title: string;
  is_read: boolean;
  created_at: string;
}

export interface NotificationPreference {
  email_on_ticket_created: boolean;
  email_on_ticket_replied: boolean;
  email_on_ticket_status_changed: boolean;
  email_on_ticket_closed: boolean;
  email_on_ticket_assigned: boolean;
}

export interface TicketStats {
  total: number;
  by_status: Record<string, number>;
  by_priority: Record<string, number>;
  sla_breached: number;
  unassigned: number;
  avg_satisfaction: number | null;
  total_rated: number;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  page_size: number;
  current_page: number;
  total_pages: number;
  results: T[];
}

// ========================
// Announcement Types
// ========================

export type AnnouncementType = 'system' | 'activity' | 'maintenance';
export type AnnouncementPriority = 'normal' | 'important' | 'urgent';
export type AnnouncementTargetType = 'all' | 'level';

export const ANNOUNCEMENT_TYPE_MAP: Record<AnnouncementType, string> = {
  system: '系统公告',
  activity: '活动通知',
  maintenance: '维护通知',
};

export const ANNOUNCEMENT_PRIORITY_MAP: Record<AnnouncementPriority, string> = {
  normal: '普通',
  important: '重要',
  urgent: '紧急',
};

export const ANNOUNCEMENT_PRIORITY_COLORS: Record<AnnouncementPriority, string> = {
  normal: 'bg-gray-100 text-gray-700',
  important: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

export const ANNOUNCEMENT_TYPE_COLORS: Record<AnnouncementType, string> = {
  system: 'bg-blue-100 text-blue-700',
  activity: 'bg-green-100 text-green-700',
  maintenance: 'bg-yellow-100 text-yellow-700',
};

export interface Announcement {
  id: string;
  title: string;
  content: string;
  type: AnnouncementType;
  type_display: string;
  priority: AnnouncementPriority;
  priority_display: string;
  target_type: AnnouncementTargetType;
  target_level_min: number | null;
  target_level_max: number | null;
  is_pinned: boolean;
  is_read: boolean;
  read_count?: number;
  published_at: string;
  created_by_name: string;
  created_at: string;
  updated_at?: string;
}

export interface AdminAnnouncement extends Announcement {
  is_published: boolean;
  read_count: number;
  target_user_count?: number;
  target_type_display?: string;
}
